import { computeAggregatedAndPriceTotals } from '@epilot/pricing'
import type { TFunction } from 'i18next'

import {
  getDisplayPrice,
  getExternalFeesMappings,
  getPriceMappings,
  getPriceType,
  shouldShowDecimals
} from '../../utils'
import type {
  BillingPeriod,
  Price,
  PriceItemWithBlockConfiguration,
  RecurrenceAmount,
  ShoppingCartData,
  PriceInputMappings
} from '../types'

/**
 * Gets the cart items to display. The returned quantity is the one comming from the tile. The number input block value is not considered for visualization.
 */
export const getDisplayableShoppingCartItems = (
  items?: PriceItemWithBlockConfiguration[],
  data?: ShoppingCartData
): PriceItemWithBlockConfiguration[] => {
  if (!items || !data) {
    return []
  }

  return items.map((item) => {
    const tileInputData = data?.find((productData) => {
      return (
        productData?.price._id &&
        productData?.price._id === item?._price?._id &&
        productData?.product?._id &&
        productData?.product._id === item?._product?._id
      )
    })

    return {
      ...item,
      quantity: tileInputData?.quantity || 1
    }
  })
}

export const formatPercentage = (value: number) => `${value}%`

export const getAmountDetails = ({
  showTrailingDecimalZeros,
  t,
  currency,
  sortedRecurrenceDetails,
  priceDisplayInJourneys,
  hasOnlyOnetimeRecurrences
}: {
  showTrailingDecimalZeros?: boolean
  t: TFunction
  currency?: string
  sortedRecurrenceDetails: RecurrenceAmount[]
  priceDisplayInJourneys: Price['price_display_in_journeys']
  hasOnlyOnetimeRecurrences: boolean
}) => {
  /* Placed in a standalone function which will later be used to format discounts as well */
  const formatPrice = (
    value: number | string,
    displayInJourneys?: Price['price_display_in_journeys']
  ) =>
    getDisplayPrice(
      t,
      value,
      displayInJourneys,
      showTrailingDecimalZeros,
      currency
    )

  const [mainPrice] = sortedRecurrenceDetails

  const amount = shouldShowDecimals(mainPrice?.amount_total)
    ? mainPrice?.amount_total_decimal
    : mainPrice?.amount_total

  const discountAmount = shouldShowDecimals(mainPrice?.discount_amount)
    ? mainPrice?.discount_amount_decimal
    : mainPrice?.discount_amount

  const beforeDiscountAmountTotal = shouldShowDecimals(
    mainPrice?.before_discount_amount_total
  )
    ? mainPrice?.before_discount_amount_total_decimal
    : mainPrice?.before_discount_amount_total

  const discountPercentage = mainPrice?.discount_percentage

  const priceDisplayInJourneysForDiscounts =
    priceDisplayInJourneys !== 'show_as_starting_price'
      ? priceDisplayInJourneys
      : undefined

  return {
    amountToDisplay: formatPrice(amount, priceDisplayInJourneys),
    discountAmountToDisplay:
      typeof discountPercentage !== 'undefined'
        ? formatPercentage(discountPercentage)
        : typeof discountAmount !== 'undefined'
          ? formatPrice(discountAmount, priceDisplayInJourneysForDiscounts)
          : undefined,
    beforeDiscountAmountTotalToDisplay:
      typeof beforeDiscountAmountTotal !== 'undefined'
        ? formatPrice(
            beforeDiscountAmountTotal,
            priceDisplayInJourneysForDiscounts
          )
        : undefined,
    priceType: !hasOnlyOnetimeRecurrences
      ? t(
          getPriceType(
            mainPrice?.type as Price['type'],
            mainPrice?.billing_period as BillingPeriod
          )
        )
      : undefined
  }
}

export const getAggregatedPrices = (data?: ShoppingCartData | undefined) => {
  const shoppingCartItems = data?.map((item) => {
    const blockMappings: PriceInputMappings = getPriceMappings(item)
    const externalFeesMappings = getExternalFeesMappings(item.price)

    return {
      ...item.price,
      quantity: item.quantity || 1,
      _price: item.price,
      _product: item.product,
      _coupons: item.coupons,
      blockConfiguration: item.blockConfiguration,
      ...(blockMappings.length > 0 && { price_mappings: blockMappings }),
      ...(externalFeesMappings.length > 0 && {
        external_fees_mappings: externalFeesMappings
      })
    }
  })

  return computeAggregatedAndPriceTotals(shoppingCartItems ?? [])
}
